import React, { useState, useEffect, useMemo } from 'react';
import packageJson from '../../package.json';
import { 
    List, Star, Home, X, ChevronDown, LayoutGrid, Search, Network,
    PanelLeftClose, PanelLeftOpen, ChevronsLeft, ChevronRight
} from 'lucide-react';

const getModuleAccent = (colorStr) => {
    if (!colorStr) return '#2563eb';
    if (colorStr.includes('blue')) return '#2563eb';
    if (colorStr.includes('indigo')) return '#4f46e5';
    if (colorStr.includes('emerald') || colorStr.includes('green')) return '#059669';
    if (colorStr.includes('teal') || colorStr.includes('cyan')) return '#0d9488';
    if (colorStr.includes('orange') || colorStr.includes('amber')) return '#d7831f';
    if (colorStr.includes('red') || colorStr.includes('rose')) return '#dc2626';
    if (colorStr.includes('purple') || colorStr.includes('violet')) return '#7c3aed';
    return '#2563eb';
};

export const Sidebar = ({ 
    isCollapsed, 
    forceSidebarOpen, 
    setForceSidebarOpen, 
    isHidden: propIsHidden,
    setIsHidden: propSetIsHidden,
    hideMenuNumbers: propHideMenuNumbers,
    activeModule, 
    setActiveModule, 
    expandedSidebar, 
    setExpandedSidebar, 
    displayModules, 
    handleOpenModule, 
    handleDrop, 
    setDraggedItem, 
    sidebarLogo, 
    currentUser, 
    theme,
    setTheme,
    isDarkMode, 
    setIsDarkMode, 
    onLogout,
    userFavorites = [],
    handleToggleFavorite
}) => {
    const [internalHidden, setInternalHidden] = useState(() => localStorage.getItem('hub_sidebar_hidden') === 'true');
    const isHidden = propIsHidden !== undefined ? propIsHidden : internalHidden;
    const setIsHidden = (val) => {
        localStorage.setItem('hub_sidebar_hidden', String(val));
        if (propSetIsHidden) propSetIsHidden(val);
        else setInternalHidden(val);
    };

    const [internalHideMenuNumbers, setInternalHideMenuNumbers] = useState(() => localStorage.getItem('apex_hide_menu_numbers') === 'true');
    const hideMenuNumbers = propHideMenuNumbers !== undefined ? propHideMenuNumbers : internalHideMenuNumbers;

    useEffect(() => {
        const handleSync = () => {
            setInternalHideMenuNumbers(localStorage.getItem('apex_hide_menu_numbers') === 'true');
        };
        window.addEventListener('storage', handleSync);
        window.addEventListener('apex_hide_menu_numbers_changed', handleSync);
        return () => {
            window.removeEventListener('storage', handleSync);
            window.removeEventListener('apex_hide_menu_numbers_changed', handleSync);
        };
    }, []);

    const [searchQuery, setSearchQuery] = useState('');
    const normalizedQuery = searchQuery.trim().toLowerCase();

    // Módulos raiz filtrados pela busca
    const filteredRootModules = useMemo(() => {
        const rootMods = displayModules
            .filter(m => m.moduleType !== 'externo' && !m.parentId)
            .sort((a, b) => {
                return String(a.id || '').localeCompare(String(b.id || ''), undefined, { numeric: true, sensitivity: 'base' });
            });

        if (!normalizedQuery) return rootMods;

        return rootMods.filter(mod => {
            const modId = String(mod.id || '').toLowerCase();
            const modTitle = String(mod.title || '').toLowerCase();
            const parentMatches = modId.includes(normalizedQuery) || modTitle.includes(normalizedQuery);
            
            const children = displayModules.filter(child => child.parentId === mod.id);
            const childMatches = children.some(child => 
                String(child.id || '').toLowerCase().includes(normalizedQuery) ||
                String(child.title || '').toLowerCase().includes(normalizedQuery)
            );

            return parentMatches || childMatches;
        });
    }, [displayModules, normalizedQuery]);

    const isFavVisible = !normalizedQuery || 'favoritos'.includes(normalizedQuery);
    const isHomeVisible = !normalizedQuery || 'início'.includes(normalizedQuery) || 'inicio'.includes(normalizedQuery) || 'hub'.includes(normalizedQuery);

    if (isHidden) {
        return (
            <button
                onClick={() => setIsHidden(false)}
                className={`fixed left-0 top-16 z-[120] py-3 px-1 rounded-r-xl border border-l-0 shadow-2xl transition-all duration-300 group flex flex-col items-center justify-center gap-1.5 cursor-pointer select-none ${
                    theme === 'light'
                        ? 'bg-[#fffdf8] hover:bg-[#fff8ee] border-[#dac9b5] text-[#077a68] shadow-slate-300/70'
                        : theme === 'acad'
                        ? 'bg-[#061f1c] hover:bg-[#0b2f2a] border-[#1e6b5c] text-[#2dd4bf] shadow-slate-950/90'
                        : 'bg-slate-900 hover:bg-slate-800 border-slate-700/80 text-blue-400 hover:text-blue-300 shadow-slate-950/90'
                }`}
                title="Exibir Menu Lateral (Hub)"
            >
                <ChevronRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
                <span className="[writing-mode:vertical-lr] rotate-180 text-[9px] font-black uppercase tracking-wider opacity-70 group-hover:opacity-100">
                    HUB
                </span>
            </button>
        );
    }

    return (
        <aside 
            className={`flex flex-col bg-slate-900 border-r border-slate-800 shadow-2xl transition-all duration-300 ease-in-out z-[999] relative shrink-0
            ${isCollapsed ? 'w-[72px]' : 'w-64'}`}
        >
            {/* CABEÇALHO DA BARRA LATERAL COM VERSÃO */}
            <div className={`flex items-center shrink-0 border-b ${
                theme === 'light' 
                    ? 'border-[#dac9b5]/60' 
                    : theme === 'acad' 
                    ? 'border-[#1b5e54]/60' 
                    : 'border-slate-800/60'
            } ${isCollapsed ? 'py-3 px-1.5 flex-col justify-center gap-2' : 'h-16 px-4 justify-between'}`}>
                {isCollapsed ? (
                    <div className="flex flex-col items-center justify-center gap-2 w-full">
                        <button 
                            onClick={() => setForceSidebarOpen(true)}
                            className={`w-9 h-9 rounded-xl flex items-center justify-center shadow-sm hover:scale-105 transition-transform ${
                                theme === 'light' ? 'bg-[#077a68] text-white' : 'bg-slate-800 text-white'
                            }`}
                            title="Expandir Menu"
                        >
                            {sidebarLogo ? (
                                <img src={sidebarLogo} alt="Logo" className="max-w-[28px] max-h-7 object-contain" />
                            ) : (
                                <Network size={20} className="text-white" />
                            )}
                        </button>
                        <div className="flex flex-col items-center gap-1">
                            {/* Botão Expandir Menu */}
                            <button 
                                onClick={() => setForceSidebarOpen(true)} 
                                className="w-7 h-7 rounded-lg flex items-center justify-center transition-all hover:bg-slate-200 dark:hover:bg-slate-800 text-blue-500"
                                title="Expandir Menu"
                            >
                                <PanelLeftOpen size={18} className="text-blue-500" />
                            </button>
                            {/* Botão Ocultar Menu (Tela Cheia) */}
                            <button 
                                onClick={() => setIsHidden(true)} 
                                className="w-7 h-7 rounded-lg flex items-center justify-center transition-all text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-800"
                                title="Ocultar Menu (Tela Cheia)"
                            >
                                <ChevronsLeft size={16} />
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center">
                            {sidebarLogo ? (
                                <img 
                                    src={sidebarLogo} 
                                    alt="Logo" 
                                    className="max-w-[36px] max-h-9 object-contain rounded-xl drop-shadow-sm shrink-0" 
                                />
                            ) : (
                                <div className="w-9 h-9 rounded-xl bg-[#077a68] text-white flex items-center justify-center shadow-sm shrink-0">
                                    <Network size={20} />
                                </div>
                            )}
                            <div className="flex flex-col ml-3 select-none">
                                <span className="text-[10px] font-bold tracking-wider text-slate-400 uppercase leading-none mb-1">
                                    MÓDULO
                                </span>
                                <div className="flex items-baseline gap-1.5 leading-none">
                                    <span className={`font-black text-base tracking-tight leading-none ${
                                        theme === 'light' ? 'text-[#10263f]' : 'text-white'
                                    }`}>
                                        HUB
                                    </span>
                                    <span className="text-xs font-normal text-slate-400 leading-none">
                                        v.{packageJson.version}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-0.5">
                            {/* Botão Recolher Menu */}
                            <button 
                                onClick={() => setForceSidebarOpen(false)} 
                                className="p-1 rounded-lg flex items-center justify-center transition-all hover:bg-slate-200 dark:hover:bg-slate-800 text-blue-500"
                                title="Recolher Menu"
                            >
                                <PanelLeftClose size={15} className="text-blue-500" />
                            </button>
                            {/* Botão Ocultar Menu (Tela Cheia) */}
                            <button 
                                onClick={() => setIsHidden(true)} 
                                className="p-1 rounded-lg flex items-center justify-center transition-all text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-800"
                                title="Ocultar Menu (Tela Cheia)"
                            >
                                <ChevronsLeft size={15} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* BARRA DE PESQUISA DOS ITENS DO MENU */}
            {!isCollapsed && (
                <div className="px-2 md:px-3 pt-2.5 pb-1 shrink-0">
                    <div className={`flex items-center px-2.5 py-1.5 rounded-xl border transition-colors ${
                        theme === 'light'
                            ? 'bg-[#fffdf8] border-[#dac9b5] text-[#10263f] focus-within:border-[#077a68]'
                            : theme === 'acad'
                            ? 'bg-[#082f2a]/80 border-[#1b5e54] text-emerald-100 focus-within:border-[#2dd4bf]'
                            : 'bg-slate-800/60 border-slate-700/80 text-slate-200 focus-within:border-slate-500'
                    }`}>
                        <Search size={14} className="text-slate-400 shrink-0 mr-2" />
                        <input 
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Buscar menu ou código..."
                            className={`hub-search-input w-full bg-transparent text-xs border-0 border-none outline-none ring-0 focus:ring-0 focus:outline-none shadow-none placeholder:text-slate-400 ${
                                theme === 'light' ? 'text-[#10263f]' : 'text-slate-100'
                            }`}
                            style={{ border: 'none', outline: 'none', boxShadow: 'none', background: 'transparent' }}
                        />
                        {searchQuery && (
                            <button 
                                onClick={() => setSearchQuery('')}
                                className="p-0.5 rounded-full hover:bg-slate-500/20 text-slate-400 hover:text-slate-600 transition-colors shrink-0"
                                title="Limpar busca"
                            >
                                <X size={12} />
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* LISTA DE ITENS DO MENU */}
            <div className="flex-1 overflow-y-auto custom-scrollbar py-2 px-2 md:px-3 space-y-1.5">
                
                {/* FAVORITOS ITEM */}
                {isFavVisible && (() => {
                    const isFavActive = activeModule === 'favorites';
                    const favWrapperClass = `facil-menu-item ${isFavActive ? 'facil-menu-active' : ''} w-full flex items-center justify-between transition-all duration-200 rounded-xl p-2 ${
                        theme === 'light'
                            ? (isFavActive ? 'bg-[#077a68] text-white border border-[#077a68] shadow-md font-bold' : 'bg-[#fffdf8] border border-[#dac9b5] text-[#10263f] font-semibold shadow-xs hover:border-[#077a68] hover:bg-[#fff8ee]')
                            : theme === 'acad'
                            ? (isFavActive ? 'bg-[#0f6f7a] text-white border border-[#2dd4bf] shadow-md font-bold' : 'bg-[#082f2a]/70 border border-[#1b5e54] text-emerald-100 font-semibold shadow-xs hover:border-[#2dd4bf]/70 hover:bg-[#0b3b35]')
                            : (isFavActive ? 'bg-[#077a68] text-white border border-teal-400 shadow-md font-bold' : 'bg-slate-800/60 border border-slate-600/80 text-slate-200 font-semibold shadow-xs hover:border-slate-400 hover:bg-slate-800')
                    }`;

                    return (
                        <div className={favWrapperClass}>
                            <button
                                onClick={() => setActiveModule('favorites')}
                                title={isCollapsed ? "Favoritos" : ""}
                                className={`flex-1 flex items-center ${isCollapsed ? 'justify-center py-0.5' : 'py-0.5'}`}
                            >
                                <div className="facil-menu-icon-wrap w-7 h-7 rounded-lg flex items-center justify-center border border-amber-500/40 bg-amber-500/10 text-amber-500 shrink-0">
                                    <Star size={16} className="fill-current text-amber-500" />
                                </div>
                                {!isCollapsed && (
                                    <span 
                                        className={`ml-2.5 text-xs text-left flex-1 truncate transition-colors ${
                                            isFavActive ? 'font-bold text-white' : (theme === 'light' ? 'font-semibold text-[#10263f]' : 'font-semibold text-slate-200')
                                        }`}
                                    >
                                        Favoritos
                                    </span>
                                )}
                            </button>
                        </div>
                    );
                })()}

                {/* INÍCIO (HUB) ITEM */}
                {isHomeVisible && (() => {
                    const isHomeActive = activeModule === null;
                    const homeWrapperClass = `facil-menu-item ${isHomeActive ? 'facil-menu-active' : ''} w-full flex items-center justify-between transition-all duration-200 rounded-xl p-2 ${
                        theme === 'light'
                            ? (isHomeActive ? 'bg-[#077a68] text-white border border-[#077a68] shadow-md font-bold' : 'bg-[#fffdf8] border border-[#dac9b5] text-[#10263f] font-semibold shadow-xs hover:border-[#077a68] hover:bg-[#fff8ee]')
                            : theme === 'acad'
                            ? (isHomeActive ? 'bg-[#0f6f7a] text-white border border-[#2dd4bf] shadow-md font-bold' : 'bg-[#082f2a]/70 border border-[#1b5e54] text-emerald-100 font-semibold shadow-xs hover:border-[#2dd4bf]/70 hover:bg-[#0b3b35]')
                            : (isHomeActive ? 'bg-[#077a68] text-white border border-teal-400 shadow-md font-bold' : 'bg-slate-800/60 border border-slate-600/80 text-slate-200 font-semibold shadow-xs hover:border-slate-400 hover:bg-slate-800')
                    }`;

                    return (
                        <div className={homeWrapperClass}>
                            <button
                                onClick={() => setActiveModule(null)}
                                title={isCollapsed ? "Início (Hub)" : ""}
                                className={`flex-1 flex items-center ${isCollapsed ? 'justify-center py-0.5' : 'py-0.5'}`}
                            >
                                <div 
                                    className="facil-menu-icon-wrap w-7 h-7 rounded-lg flex items-center justify-center border shrink-0 transition-colors"
                                    style={{
                                        borderColor: isHomeActive ? '#ffffff' : (theme === 'light' ? '#3b82f640' : '#3b82f660'),
                                        backgroundColor: isHomeActive ? 'transparent' : 'rgba(59,130,246,0.1)',
                                        color: isHomeActive ? '#ffffff' : '#3b82f6'
                                    }}
                                >
                                    <Home size={16} className={isHomeActive ? 'text-white' : 'text-blue-400'} />
                                </div>
                                {!isCollapsed && (
                                    <span 
                                        className={`ml-2.5 text-xs text-left flex-1 truncate transition-colors ${
                                            isHomeActive ? 'font-bold text-white' : (theme === 'light' ? 'font-semibold text-[#10263f]' : 'font-semibold text-slate-200')
                                        }`}
                                    >
                                        Início (Hub)
                                    </span>
                                )}
                            </button>
                        </div>
                    );
                })()}

                {(isFavVisible || isHomeVisible) && (
                    <div className="h-px bg-slate-800/60 my-2 mx-2"></div>
                )}

                {/* LISTA DE MÓDULOS */}
                {filteredRootModules
                    .map(mod => {
                        const isActive = activeModule?.id === mod.id;
                        const children = displayModules
                            .filter(child => child.parentId === mod.id)
                            .sort((a, b) => (a.order || 99) - (b.order || 99))
                            .filter(child => {
                                if (!normalizedQuery) return true;
                                const modId = String(mod.id || '').toLowerCase();
                                const modTitle = String(mod.title || '').toLowerCase();
                                if (modId.includes(normalizedQuery) || modTitle.includes(normalizedQuery)) return true;
                                return String(child.id || '').toLowerCase().includes(normalizedQuery) ||
                                       String(child.title || '').toLowerCase().includes(normalizedQuery);
                            });
                        const hasChildren = children.length > 0;
                        const isExpanded = normalizedQuery ? true : expandedSidebar[mod.id];
                        const isFav = userFavorites.some(f => f.moduleId === mod.id);
                        const accentColor = getModuleAccent(mod.color);

                        // Container classes com altura padronizada p-2 e bordas destacadas
                        const itemWrapperClass = `facil-menu-item ${isActive ? 'facil-menu-active' : ''} group w-full flex items-center justify-between transition-all duration-200 rounded-xl p-2 ${
                            theme === 'light'
                                ? (isActive ? 'bg-[#077a68] text-white border border-[#077a68] shadow-md font-bold' : 'bg-[#fffdf8] border border-[#dac9b5] text-[#10263f] font-semibold shadow-xs hover:border-[#077a68] hover:bg-[#fff8ee]')
                                : theme === 'acad'
                                ? (isActive ? 'bg-[#0f6f7a] text-white border border-[#2dd4bf] shadow-md font-bold' : 'bg-[#082f2a]/70 border border-[#1b5e54] text-emerald-100 font-semibold shadow-xs hover:border-[#2dd4bf]/70 hover:bg-[#0b3b35]')
                                : (isActive ? 'bg-[#077a68] text-white border border-teal-400 shadow-md font-bold' : 'bg-slate-800/60 border border-slate-600/80 text-slate-200 font-semibold shadow-xs hover:border-slate-400 hover:bg-slate-800')
                        }`;

                        return (
                            <div key={mod.id} className="flex flex-col w-full">
                                <div className={itemWrapperClass}>
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => {
                                            if (hasChildren) {
                                                setExpandedSidebar(prev => ({...prev, [mod.id]: !prev[mod.id]}));
                                                setForceSidebarOpen(true);
                                                
                                                if (mod.url && mod.openMode !== 'none') {
                                                    if ((mod.openMode === 'newTab' || mod.openInNewTab)) {
                                                        window.open(mod.url, '_blank');
                                                    } else {
                                                        handleOpenModule(mod);
                                                    }
                                                }
                                            } else {
                                                handleOpenModule(mod);
                                            }
                                        }}
                                        draggable="true"
                                        onDragStart={(e) => { e.dataTransfer.setData('text/plain', mod.id); setDraggedItem(mod); }}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={(e) => handleDrop(e, mod, false)}
                                        title={isCollapsed ? `${mod.id} - ${mod.title}` : (!hideMenuNumbers ? `${mod.id} - ${mod.title}` : mod.title)}
                                        className={`flex-1 flex items-center cursor-pointer select-none ${isCollapsed ? 'justify-center py-0.5' : 'py-0.5'}`}
                                    >
                                        <div 
                                            className="facil-menu-icon-wrap w-7 h-7 rounded-lg flex items-center justify-center border shrink-0 transition-colors"
                                            style={{
                                                borderColor: isActive ? '#ffffff' : `${accentColor}40`,
                                                backgroundColor: isActive ? 'transparent' : `${accentColor}15`,
                                                color: isActive ? '#ffffff' : accentColor
                                            }}
                                        >
                                            {mod.customIconUrl ? (
                                                <img src={mod.customIconUrl} className="w-4 h-4 object-contain" alt="icon" />
                                            ) : (
                                                React.createElement(mod.icon || LayoutGrid, { 
                                                    size: 16,
                                                    style: { stroke: isActive ? '#ffffff' : accentColor }
                                                })
                                            )}
                                        </div>
                                        
                                        {!isCollapsed && (
                                            <>
                                                {/* Badge do Código com Estrela de Favorito */}
                                                {!hideMenuNumbers ? (
                                                    <div className="relative shrink-0 flex items-center justify-center ml-2">
                                                        <span 
                                                            className="facil-menu-number flex items-center justify-center w-5 h-5 rounded-md text-[10px] font-mono shrink-0 font-bold border transition-all"
                                                            style={{
                                                                borderColor: isActive ? '#ffffff' : (theme === 'light' ? '#bfdbfe' : (theme === 'acad' ? '#1b5e54' : '#475569')),
                                                                backgroundColor: isActive ? '#ffffff' : (theme === 'light' ? '#eff6ff' : (theme === 'acad' ? '#062421' : '#1e293b')),
                                                                color: isActive ? '#077a68' : (theme === 'light' ? '#1d4ed8' : (theme === 'acad' ? '#5eead4' : '#93c5fd'))
                                                            }}
                                                        >
                                                            {mod.id}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleToggleFavorite(e, mod);
                                                            }}
                                                            className={`absolute -top-1.5 -right-1.5 z-30 transition-all duration-150 p-0 cursor-pointer select-none leading-none flex items-center justify-center ${
                                                                isFav
                                                                    ? "opacity-100 scale-100 drop-shadow-sm"
                                                                    : "opacity-0 group-hover:opacity-100 group-hover:scale-100 hover:scale-125"
                                                            }`}
                                                            title={isFav ? "Remover dos Favoritos" : "Adicionar aos Favoritos"}
                                                        >
                                                            <Star
                                                                size={9.5}
                                                                className={
                                                                    isFav
                                                                        ? "text-amber-400 fill-amber-400 drop-shadow-[0_1px_2px_rgba(251,191,36,0.6)]"
                                                                        : "text-slate-400 dark:text-slate-300 fill-none stroke-[2.2] hover:text-amber-400"
                                                                    }
                                                            />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleToggleFavorite(e, mod);
                                                        }}
                                                        className={`ml-2 shrink-0 transition-all duration-150 p-0.5 cursor-pointer select-none leading-none flex items-center justify-center ${
                                                            isFav
                                                                ? "opacity-100 scale-100 drop-shadow-sm"
                                                                : "opacity-0 group-hover:opacity-100 group-hover:scale-100 hover:scale-125"
                                                        }`}
                                                        title={isFav ? "Remover dos Favoritos" : "Adicionar aos Favoritos"}
                                                    >
                                                        <Star
                                                            size={12}
                                                            className={
                                                                isFav
                                                                    ? "text-amber-400 fill-amber-400 drop-shadow-[0_1px_2px_rgba(251,191,36,0.6)]"
                                                                    : "text-slate-400 dark:text-slate-300 fill-none stroke-[2.2] hover:text-amber-400"
                                                            }
                                                        />
                                                    </button>
                                                )}

                                                <span 
                                                    className="ml-2.5 text-xs text-left flex-1 truncate transition-colors"
                                                    style={{
                                                        color: isActive ? '#ffffff' : (theme === 'light' ? '#10263f' : (theme === 'acad' ? '#e2e8f0' : '#cbd5e1'))
                                                    }}
                                                >
                                                    {mod.title}
                                                </span>
                                                {hasChildren && (
                                                    <ChevronDown size={14} className={`shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} style={{ color: isActive ? '#ffffff' : '#64748b' }} />
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* RENDERIZAÇÃO DOS SUBMENUS */}
                                {hasChildren && isExpanded && !isCollapsed && (
                                    <div className="flex flex-col gap-1.5 mt-1 ml-4 pl-3 border-l-2 border-slate-800/60 animate-fade-in">
                                        {children.map(child => {
                                            const isChildActive = activeModule?.id === child.id;
                                            const isChildFav = userFavorites.some(f => f.moduleId === child.id);
                                            
                                            return (
                                                <div
                                                    key={child.id}
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => handleOpenModule(child)}
                                                    title={!hideMenuNumbers ? `${child.id} - ${child.title}` : child.title}
                                                    className={`w-full flex items-center rounded-lg transition-all duration-200 group px-3 py-1.5 border-l-2 cursor-pointer select-none ${
                                                        isChildActive 
                                                            ? (theme === 'light' ? 'bg-[#0f6f7a] text-white border-[#0f6f7a]' : 'bg-slate-800 text-white border-blue-400') 
                                                            : (theme === 'light' ? 'text-[#58687c] hover:bg-[#fff8ee] border-transparent' : 'text-slate-400 hover:bg-slate-800/40 border-transparent')
                                                    } -ml-[2px]`}
                                                >
                                                    {child.customIconUrl ? (
                                                        <img src={child.customIconUrl} className="w-4 h-4 object-contain shrink-0" alt="icon" />
                                                    ) : (
                                                        React.createElement(child.icon || LayoutGrid, { 
                                                            size: 16, 
                                                            className: "shrink-0 transition-colors",
                                                            style: { stroke: isChildActive ? '#ffffff' : (theme === 'light' ? '#58687c' : '#64748b') }
                                                        })
                                                    )}
                                                    {!hideMenuNumbers ? (
                                                        <div className="relative shrink-0 flex items-center justify-center ml-2">
                                                            <span 
                                                                className="text-[10px] font-mono px-1.5 py-0.5 rounded shrink-0"
                                                                style={{
                                                                    backgroundColor: isChildActive ? 'rgba(255,255,255,0.15)' : (theme === 'light' ? '#fff8ee' : '#1e293b'),
                                                                    border: `1px solid ${isChildActive ? 'rgba(255,255,255,0.25)' : (theme === 'light' ? '#dac9b5' : '#334155')}`,
                                                                    color: isChildActive ? '#ffffff' : (theme === 'light' ? '#58687c' : '#94a3b8')
                                                                }}
                                                            >
                                                                {child.id}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleToggleFavorite(e, child);
                                                                }}
                                                                className={`absolute -top-1.5 -right-1.5 z-30 transition-all duration-150 p-0 cursor-pointer select-none leading-none flex items-center justify-center ${
                                                                    isChildFav
                                                                        ? "opacity-100 scale-100 drop-shadow-sm"
                                                                        : "opacity-0 group-hover:opacity-100 group-hover:scale-100 hover:scale-125"
                                                                }`}
                                                                title={isChildFav ? "Remover dos Favoritos" : "Adicionar aos Favoritos"}
                                                            >
                                                                <Star
                                                                    size={9.5}
                                                                    className={
                                                                        isChildFav
                                                                            ? "text-amber-400 fill-amber-400 drop-shadow-[0_1px_2px_rgba(251,191,36,0.6)]"
                                                                            : "text-slate-400 dark:text-slate-300 fill-none stroke-[2.2] hover:text-amber-400"
                                                                    }
                                                                />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleToggleFavorite(e, child);
                                                            }}
                                                            className={`ml-2 shrink-0 transition-all duration-150 p-0.5 cursor-pointer select-none leading-none flex items-center justify-center ${
                                                                isChildFav
                                                                    ? "opacity-100 scale-100 drop-shadow-sm"
                                                                    : "opacity-0 group-hover:opacity-100 group-hover:scale-100 hover:scale-125"
                                                            }`}
                                                            title={isChildFav ? "Remover dos Favoritos" : "Adicionar aos Favoritos"}
                                                        >
                                                            <Star
                                                                size={12}
                                                                className={
                                                                    isChildFav
                                                                        ? "text-amber-400 fill-amber-400 drop-shadow-[0_1px_2px_rgba(251,191,36,0.6)]"
                                                                        : "text-slate-400 dark:text-slate-300 fill-none stroke-[2.2] hover:text-amber-400"
                                                                }
                                                            />
                                                        </button>
                                                    )}
                                                    <span 
                                                        className="ml-2 font-bold text-xs text-left flex-1 truncate"
                                                        title={!hideMenuNumbers ? `${child.id} - ${child.title}` : child.title}
                                                    >
                                                        {child.title}
                                                    </span>
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                {normalizedQuery && filteredRootModules.length === 0 && !isFavVisible && !isHomeVisible && (
                    <div className="text-center py-8 px-4 text-xs text-slate-400">
                        Nenhum menu encontrado para "{searchQuery}"
                    </div>
                )}
            </div>
        </aside>
    );
};

export default Sidebar;
